import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
// TODO: dropdown left to clean up
import { Dropdown, Form } from "semantic-ui-react";

import { DATE_FORMATS_FNS, useCan, useSelectedLocation } from "components";
import { FIXED_VIN_LENGTH } from "modules/Appointments/components/NewAppointment/utils";
import { useAddInterventionPendingChecklistMutation } from "modules/PendingChecklistsDashboard/components/InterventionChecklists/components/InterventionChecklistsModal/components/AddInterventionPendingChecklist/hooks";
import { useChecklistDropdownOptions } from "modules/PendingChecklistsDashboard/hooks";
import { formatCurrentUTCDate } from "utils/dateHelperFunctions";

import type { Checklist } from "models";
import type { SortableInterventionPendingChecklists } from "modules/PendingChecklistsDashboard/components/InterventionChecklists/components";
import type { InterventionPendingChecklistsGrouped } from "modules/PendingChecklistsDashboard/components/InterventionChecklists/hooks";
import type { GroupedChecklistsDropdownOptions } from "modules/PendingChecklistsDashboard/hooks";

type AddInterventionPendingChecklistProps = {
  keyword: string | null;
  sortableInterventionPendingChecklists: SortableInterventionPendingChecklists[];
  checklists?: Checklist[];
  pendingChecklistsGrouped?: InterventionPendingChecklistsGrouped[];
  onSetSelectedInterventionKeyword: (keyword: string) => void;
};

export const AddInterventionPendingChecklist = ({
  keyword,
  checklists,
  pendingChecklistsGrouped = [],
  sortableInterventionPendingChecklists,
  onSetSelectedInterventionKeyword
}: AddInterventionPendingChecklistProps) => {
  const { t } = useTranslation();

  const { selectedLocation } = useSelectedLocation();
  const { mutateAsync: addInterventionPendingChecklist } = useAddInterventionPendingChecklistMutation();
  const canAddPendingChecklist = useCan("create", "intervention-keywords-pending-checklist");
  const { groupedChecklistsDropdownOptions } = useChecklistDropdownOptions(checklists);
  const dropdownOptions = groupedChecklistsDropdownOptions.map(({ infoText: _, ...option }) => option);

  const [vinPrefix, setVinPrefix] = useState("");
  const [selectedChecklistId, setSelectedChecklistId] = useState(0);

  const excludeVinLessChecklistsDropdownOptions = dropdownOptions.reduce<GroupedChecklistsDropdownOptions[]>((acc, cdo) => {
    if (!sortableInterventionPendingChecklists.some(i => i.checklist_id === cdo.key && i.vin_prefix === "")) acc.push(cdo);
    return acc;
  }, []);
  const nextOrdering = sortableInterventionPendingChecklists.length + 1;

  const canAddInterventionPendingChecklist = () => {
    if (!canAddPendingChecklist || !selectedChecklistId || !keyword) return false;

    const vinExceedsMaxLength = vinPrefix.length > FIXED_VIN_LENGTH;
    const duplicateWithoutVin = !vinPrefix && sortableInterventionPendingChecklists.some(cl => cl.checklist_id === selectedChecklistId);
    if (vinExceedsMaxLength || duplicateWithoutVin) return false;

    return true;
  };

  const isInterventionTitleDuplicate = () => {
    if (pendingChecklistsGrouped.some(pcg => pcg.keyword === keyword && nextOrdering === 1)) {
      toast.error(t("v8_this_intervention_title_is_already_added", "This intervention title is already added"));
      return true;
    }

    if (sortableInterventionPendingChecklists.some(sipcg => sipcg.keyword === keyword && sipcg.checklist_id === selectedChecklistId && sipcg.vin_prefix === vinPrefix)) {
      toast.error(t("v8_this_checklist_already_exists_for_this_vin_number", "This checklist already exists for this VIN number."));
      return true;
    }

    return false;
  };

  const handleAddInterventionPendingChecklist = async () => {
    if (isInterventionTitleDuplicate()) return;

    if (selectedChecklistId) {
      const selectedChecklist = checklists?.find(cl => cl.id === selectedChecklistId);

      if (selectedChecklist && selectedLocation && keyword) {
        setSelectedChecklistId(0);
        setVinPrefix("");
        await addInterventionPendingChecklist({
          keyword,
          ordering: nextOrdering,
          vin_prefix: vinPrefix,
          checklist_id: selectedChecklist.id,
          checklist_name: selectedChecklist.name,
          dealer_location_id: selectedLocation.id,
          created_on: formatCurrentUTCDate(DATE_FORMATS_FNS.ISOFormat),
          updated_on: formatCurrentUTCDate(DATE_FORMATS_FNS.ISOFormat)
        });
        onSetSelectedInterventionKeyword(keyword);
      }
    }
  };

  return (
    <Form>
      <div className="add-checklists-container">
        <div className="full-width">
          <Dropdown
            fluid
            selection
            clearable
            search
            selectOnBlur={false}
            disabled={!canAddPendingChecklist}
            options={excludeVinLessChecklistsDropdownOptions}
            value={selectedChecklistId > 0 ? selectedChecklistId : ""}
            placeholder={t("v8_select_mandatory_checklists", "Select mandatory checklists")}
            onChange={(_e, { value }) => setSelectedChecklistId(Number(value) || 0)}
          />
        </div>
        <div className="full-width">
          <Form.Field error={vinPrefix.length > FIXED_VIN_LENGTH}>
            <Form.Input fluid value={vinPrefix} placeholder={`${t("v8_enter_vin", "Enter VIN")} #`} onChange={(_e, { value }) => setVinPrefix(value)} />
          </Form.Field>
        </div>
        <Form.Button type="button" color="green" className="add-button" onClick={handleAddInterventionPendingChecklist} disabled={!canAddInterventionPendingChecklist()}>
          {t("v8_add", "Add")}
        </Form.Button>
      </div>
    </Form>
  );
};
